import React, { useCallback, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { useLabels } from 'ws-scripts/modules/common';
import { selectFeaturedItems } from '../features/navDataSlice';
import { useCurrentPath } from '../context/CurrentPathContext';
import useFeaturedOverflow from '../hooks/useFeaturedOverflow';
import useFeaturedMegaMenuState from '../hooks/useFeaturedMegaMenuState';
import useRefMap from '../hooks/useRefMap';
import { hasActiveDescendant, isNavItemActive } from '../utils/navItemUtils';
import FeaturedNavItem from './FeaturedNavItem';
import MegaMenu from './MegaMenu';

const isHoverCapable = () =>
	typeof window !== 'undefined' &&
	typeof window.matchMedia === 'function' &&
	window.matchMedia('(hover: hover)').matches;

/**
 * True when a featured item's trigger should render active: its own mega
 * menu is open, the current page matches the item itself (plain links), or
 * matches one of its descendants (mega menu items, via hasActiveDescendant).
 *
 * @param {NavigationItem} item
 * @param {{ openMegaMenuId: string|null, currentPath: string, isMegaMenuItem: boolean }} context
 * @returns {boolean}
 */
const getIsActive = (item, { openMegaMenuId, currentPath, isMegaMenuItem }) => {
	if (openMegaMenuId === item.id) return true;
	return isMegaMenuItem
		? hasActiveDescendant(item, currentPath)
		: isNavItemActive(item, currentPath);
};

const FeaturedNavBar = ({ closeMegaMenuRef }) => {
	const labels = useLabels();
	const featuredItems = useSelector(selectFeaturedItems);
	const currentPath = useCurrentPath();
	const containerRef = useRef(null);
	const visibleCount = useFeaturedOverflow(
		containerRef,
		featuredItems.length
	);
	const triggerRefs = useRefMap(featuredItems.map((item) => item.id));
	const hoverCapable = useMemo(() => isHoverCapable(), []);
	const visibleItems = featuredItems.slice(0, visibleCount);

	// Tracks the id of a mega menu whose most recent open was triggered by
	// keyboard activation (Enter/Space), not a mouse click — set by
	// handleTriggerClick, consumed and cleared by the corresponding MegaMenu
	// once it moves focus onto its first item (see onAutoFocusHandled below).
	const [keyboardOpenedMenuId, setKeyboardOpenedMenuId] = useState(null);

	const {
		openMegaMenuId,
		megaMenuItems,
		hoverIntent,
		handleClose,
		handleItemClick,
		handleItemHoverEnter,
		handleFocusLeave
	} = useFeaturedMegaMenuState(featuredItems, closeMegaMenuRef);

	const handleTriggerClick = useCallback(
		(item, event) => {
			const isOpening = openMegaMenuId !== item.id;
			handleItemClick(item);
			// A real mouse click reports detail >= 1; a click synthesized from
			// keyboard activation (Enter/Space) reports detail === 0 — only the
			// latter should move focus into the panel it just opened.
			if (isOpening && event?.detail === 0) {
				setKeyboardOpenedMenuId(item.id);
			}
		},
		[openMegaMenuId, handleItemClick]
	);

	const clearKeyboardOpenedMenuId = useCallback(() => {
		setKeyboardOpenedMenuId(null);
	}, []);

	const focusNextTopLevelTrigger = useCallback(
		(itemId) => {
			const idx = visibleItems.findIndex((i) => i.id === itemId);
			const nextItem = visibleItems[idx + 1];
			if (!nextItem) return false;
			triggerRefs.current[nextItem.id]?.current?.focus();
			return true;
		},
		[visibleItems, triggerRefs]
	);

	if (featuredItems.length === 0) return null;

	return (
		<nav
			aria-label={labels.get('MAIN_NAV_LABEL') || 'Main navigation'}
			className="ws-nav-drawer__featured-bar-wrapper"
		>
			<div
				ref={containerRef}
				className="ws-nav-drawer__featured-bar d-flex align-items-center overflow-hidden"
			>
				{visibleItems.map((item) => {
					const isMegaMenuItem = item.columns?.length > 0;
					const isActive = getIsActive(item, {
						openMegaMenuId,
						currentPath,
						isMegaMenuItem
					});
					// Only mega menu items get hover-intent wiring — plain links
					// have nothing to open on hover. Focus is deliberately not
					// wired here: Tab-focusing a trigger must not open its menu
					// (see useHoverOpenIntent); opening on keyboard activation is
					// handled explicitly by handleTriggerClick below.
					const hoverHandlers = isMegaMenuItem
						? {
								onMouseEnter: hoverCapable
									? () => handleItemHoverEnter(item)
									: undefined,
								onMouseLeave: hoverCapable
									? hoverIntent.onMouseLeave
									: undefined,
								onBlur: hoverIntent.onBlur
						  }
						: {};

					return (
						<FeaturedNavItem
							key={item.id}
							ref={triggerRefs.current[item.id]}
							item={item}
							isActive={isActive}
							isMegaMenuOpen={openMegaMenuId === item.id}
							onClick={handleTriggerClick}
							{...hoverHandlers}
						/>
					);
				})}
			</div>
			{megaMenuItems.map((item) => (
				<MegaMenu
					key={item.id}
					item={item}
					visible={openMegaMenuId === item.id}
					onClose={handleClose}
					triggerRef={triggerRefs.current[item.id]}
					autoFocusFirstItem={keyboardOpenedMenuId === item.id}
					onAutoFocusHandled={clearKeyboardOpenedMenuId}
					onFocusNextTrigger={focusNextTopLevelTrigger}
					onFocusLeave={handleFocusLeave}
				/>
			))}
		</nav>
	);
};

FeaturedNavBar.propTypes = {
	closeMegaMenuRef: PropTypes.shape({ current: PropTypes.func })
};

FeaturedNavBar.defaultProps = {
	closeMegaMenuRef: null
};

export default FeaturedNavBar;
